<template>
    <div class="app-body" id="view">
        <el-form
            ref="agent_settings"
            class="aloai-agents form-aloware"
            :rules="rules"
            :model="bot"
            data-testid="aloai-agent-settings-form"
        >
            <div v-if="shouldShowAloAi" class="container-listing position-relative">
                <div class="container-header d-flex justify-content-between position-sticky"
                     :style="{top: (top_offset+50) + 'px'}">
                    <div class="d-flex">
                        <el-button
                            type="info"
                            size="medium"
                            :disabled="loading"
                            round
                            @click="gotoBots">
                            <i class="fa fa-arrow-left mr-1"></i>
                            <strong>Back</strong>
                        </el-button>

                        <div class="listing-heading ml-3">
                            <div class="bot-title">{{ isFormLoaded ? dialogTitle : 'Loading...' }}</div>
                            <div class="bot-subtitle">{{ isFormLoaded ? dialogSubtitle : '' }}</div>
                        </div>
                    </div>

                    <div v-if="!bot.is_externally_managed" class="d-flex">
                        <el-button
                            v-if="bot.id && bot.type === AloAi.TYPE_VOICE"
                            type="success"
                            :disabled="loading"
                            @click="handleTestCall"
                        >
                            <i class="fa fa-phone mr-1"></i>
                            Test
                        </el-button>

                        <el-button type="primary"
                                   :disabled="loading"
                                   @click="submitForm">
                            <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
                            {{ submitButtonText }}
                        </el-button>
                    </div>
                </div>

                <!-- Loading Indicator -->
                <div v-if="!isFormLoaded" class="d-flex justify-content-center align-items-center py-5">
                    <div class="text-center">
                        <i class="fa fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                        <p class="text-muted">{{ loading ? (bot.id ? 'Updating agent...' : 'Creating agent...') : 'Loading...' }}</p>
                    </div>
                </div>

                <!-- Externally managed banner -->
                <div v-if="isFormLoaded && bot.is_externally_managed" class="container-fluid p-0 mb-3">
                    <div class="alert alert-info d-flex align-items-center mb-0" role="alert">
                        <i class="fa fa-external-link mr-2"></i>
                        <span>This agent is externally managed. To edit its configuration, use the external platform's dashboard.</span>
                    </div>
                </div>

                <!-- Main Content - Only show when form is loaded and not externally managed -->
                <div v-if="isFormLoaded && !bot.is_externally_managed" class="container-fluid d-relative p-0">
                    <div>
                        <el-tabs
                            v-model="activeName"
                            tab-position="left"
                            type="border-card"
                            id="account-settings-tab"
                            data-testid="aloai-agent-settings-tabs"
                        >
                            <!-- Configure tab -->
                            <el-tab-pane
                                v-if="availableTabs.includes('configure')"
                                key="configure-tab"
                                label="Configure"
                                name="configure"
                                class="px-3 py-2"
                                data-tab-error="configure"
                                data-testid="aloai-configure-tab"
                            >
                                <span slot="label">
                                    Configure<span v-html="tabLabelErrorIcon(tab_errors.configure)"></span>
                                </span>
                                <configure-tab
                                    :bot="bot"
                                    :campaigns="campaigns"
                                    :bot-id="bot_id"
                                    :default-outbound-type="default_outbound_type"
                                    @validate="preValidateForm('agent_settings')"
                                    @outbound-type-change="default_outbound_type = $event"
                                    @pending-inbound-lines-change="pendingInboundCampaignIds = $event"
                                />
                            </el-tab-pane>

                            <!-- Instructions tab -->
                            <el-tab-pane
                                v-if="availableTabs.includes('instructions')"
                                key="instructions-tab"
                                label="Instructions"
                                name="instructions"
                                class="px-3 py-2"
                                data-tab-error="instructions"
                                data-testid="aloai-instructions-tab"
                            >
                                <span slot="label">
                                    Instructions<span v-html="tabLabelErrorIcon(tab_errors.instructions)"></span>
                                </span>
                                <prompt-tab
                                    ref="promptTab"
                                    :bot="bot"
                                    @validate="preValidateForm('agent_settings')"
                                    @save-instructions="saveInstructions"
                                    data-testid="aloai-model-settings"
                                />
                            </el-tab-pane>

                            <!-- Context tab -->
                            <el-tab-pane
                                v-if="availableTabs.includes('context')"
                                key="context-tab"
                                label="Context"
                                name="context"
                                class="px-3 py-2"
                                data-tab-error="context"
                                data-testid="aloai-context-tab"
                            >
                                <span slot="label">
                                    Context<span v-html="tabLabelErrorIcon(tab_errors.context)"></span>
                                </span>
                                <context-tab
                                    :bot="bot"
                                    data-testid="aloai-context-component"
                                />
                            </el-tab-pane>

                            <!-- Post-Call Actions tab (Voice only) -->
                            <el-tab-pane
                                v-if="availableTabs.includes('post_call_actions')"
                                key="post-call-actions-tab"
                                label="Post-Call Actions"
                                name="post_call_actions"
                                class="px-3 py-2"
                                data-tab-error="post_call_actions"
                                data-testid="aloai-post-call-actions-tab"
                            >
                                <span slot="label">
                                    Post-Call Actions<span v-html="tabLabelErrorIcon(tab_errors.post_call_actions)"></span>
                                </span>
                                <post-call-actions-tab :bot="bot"/>
                            </el-tab-pane>

                            <!-- Schedule tab -->
                            <el-tab-pane
                                v-if="availableTabs.includes('schedule')"
                                key="schedule-tab"
                                label="Schedule"
                                name="schedule"
                                class="px-3 py-2"
                                data-tab-error="schedule"
                                data-testid="aloai-schedule-tab"
                            >
                                <span slot="label">
                                    Schedule<span v-html="tabLabelErrorIcon(tab_errors.schedule)"></span>
                                </span>
                                <schedule-tab :bot="bot" data-testid="aloai-schedule-component"/>
                            </el-tab-pane>

                            <!-- Actions tab (Text only) -->
                            <el-tab-pane
                                v-if="availableTabs.includes('actions')"
                                key="actions-tab"
                                label="Actions"
                                name="actions"
                                class="px-3 py-2"
                                data-tab-error="actions"
                                data-testid="aloai-actions-tab"
                            >
                                <span slot="label">
                                    Actions<span v-html="tabLabelErrorIcon(tab_errors.actions)"></span>
                                </span>
                                <actions-tab :bot="bot" />
                            </el-tab-pane>

                            <!-- Advanced tab (Voice only) -->
                            <el-tab-pane
                                v-if="availableTabs.includes('advanced')"
                                key="advanced-tab"
                                label="Advanced"
                                name="advanced"
                                class="px-3 py-2"
                                data-tab-error="advanced"
                                data-testid="aloai-advanced-tab"
                            >
                                <span slot="label">
                                    Advanced<span v-html="tabLabelErrorIcon(tab_errors.advanced)"></span>
                                </span>
                                <advanced-tab
                                    :bot="bot"
                                />
                            </el-tab-pane>

                            <!-- Custom Functions tab -->
                            <el-tab-pane
                                v-if="availableTabs.includes('custom_functions')"
                                key="custom-functions-tab"
                                label="Custom Functions"
                                name="custom_functions"
                                class="px-3 py-2"
                                data-tab-error="custom_functions"
                            >
                                <span slot="label">
                                    Custom Functions
                                </span>
                                <custom-functions-tab
                                />
                            </el-tab-pane>
                        </el-tabs>
                    </div>
                </div>
            </div>
        </el-form>

        <test-call-dialog
            v-if="bot.id && bot.type === AloAi.TYPE_VOICE"
            :visible.sync="showTestCallDialog"
            :bot-id="bot.id"
        />
    </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import auth from '../../auth.js'
import AloAiTable from '../../components/aloai/aloai-table.vue'
import CustomFunction from '../../components/aloai/form/custom-function'
import DurationPicker from '../../components/aloai/form/duration-picker'
import ActionsTab from '../../components/aloai/form/tabs/actions-tab'
import AdvancedTab from '../../components/aloai/form/tabs/advanced-tab'
import ConfigureTab from '../../components/aloai/form/tabs/configure-tab'
import ContextTab from '../../components/aloai/form/tabs/context-tab'
import CustomFunctionsTab from '../../components/aloai/form/tabs/custom-functions-tab'
import PostCallActionsTab from '../../components/aloai/form/tabs/post-call-actions-tab'
import PromptTab from '../../components/aloai/form/tabs/prompt-tab'
import ScheduleTab from '../../components/aloai/form/tabs/schedule-tab'
import TestCallDialog from '../../components/aloai/test-call/TestCallDialog.vue'
import Variables from '../../components/messenger/variables'
import * as AloAi from "../../constants/aloai"
import { acl_mixin, aloai_mixin, campaign_mixin, contact_phone_number_mixin, form_handler, form_validation_mixin, html_mixin, scroll_mixin, styling_mixin } from '../../mixins'

export default {
    components: {
        AloAiTable,
        PromptTab,
        Variables,
        DurationPicker,
        ConfigureTab,
        AdvancedTab,
        CustomFunctionsTab,
        ActionsTab,
        ScheduleTab,
        PostCallActionsTab,
        CustomFunction,
        ContextTab,
        TestCallDialog,
    },
    mixins: [
        acl_mixin,
        html_mixin,
        styling_mixin,
        aloai_mixin,
        form_validation_mixin,
        campaign_mixin,
        contact_phone_number_mixin,
        scroll_mixin,
        form_handler
    ],
    props: {
        bot_id: {
            required: false
        }
    },
    data() {
        return {
            loading: false,
            showTestCallDialog: false,
            savedBotSnapshot: null,
            dataLoading: true, // New loading state for voice data
            botLoading: true, // Loading state for bot data
            pendingInboundCampaignIds: [], // Store pending inbound lines for new agents
            bot: {
                name: '',
                description: '',
                opener: '',
                ttl: 10080,
                type: null,
                direction: null,
                allow_access_contact_information: true,
                model: AloAi.MODEL_GPT_54_NANO,
                voice_model: AloAi.VOICE_MODEL_GPT_41_MINI,
                voice_id: null,
                language: AloAi.LANGUAGE_EN_US,
                default_outbound_campaign_id: null,
                instructions: [
                    {
                        role: 'system',
                        content: ''
                    }
                ],
                settings: {
                    retellai: {
                        fallback_voice_ids: null,
                        enable_backchannel: false,
                        end_call_after_silence_ms: 60000,
                        max_call_duration_ms: 3600000,
                        reminder_trigger_ms: 15000,
                        reminder_max_count: 3,
                        interruption_sensitivity: 0.8,
                        responsiveness: 1,
                        voice_speed: 1,
                        voice_temperature: 1,
                        volume: 1,
                        ambient_sound: null,
                    }
                },
                tools: [],
                post_call_notification: false,
                post_call_notification_mode: AloAi.POST_CALL_NOTIFICATION_MODE_DEFAULT,
                post_call_notification_users: [],
                post_call_notification_emails: [],
                voicemail_notification_users: [],
                follow_up_enabled: false,
                follow_up_frequency: 3,
                follow_up_delay: 60, // in seconds
                process_on_holidays: false,
                allow_international_outbound: false,
                operating_hours: {
                    'monday': [{'id': '1', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}],
                    'tuesday': [{'id': '2', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}],
                    'wednesday': [{'id': '3', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}],
                    'thursday': [{'id': '4', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}],
                    'friday': [{'id': '5', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}],
                    'saturday': [{'id': '6', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}],
                    'sunday': [{'id': '7', 'open': '24hrs', 'close': '24hrs', 'isOpen': true}]
                },
                enabled: true,
                // Voicemail detection settings
                voicemail_detection_enabled: false,
                voicemail_behavior: 'hang_up',
                voicemail_message: '',
                voicemail_prompt: '',
                voicemail_use_dynamic: false,
                speak_first: false,
                speak_first_delay_seconds: 0,
            },
            // Settings-related data
            rules: {
                name: [
                    {
                        required: true,
                        message: 'Please provide a name for the agent',
                        trigger: 'blur'
                    }
                ],
                opener: {
                    validator: (_, value, callback) => {
                        // Opener is optional when speak_first is enabled for outbound voice bots
                        if (this.bot.type === AloAi.TYPE_VOICE && this.bot.direction === AloAi.DIRECTION_OUTBOUND && this.bot.speak_first) {
                            callback()
                            return
                        }
                        if (!value || !value.trim()) {
                            callback(new Error('Please provide an opener for the agent'))
                            return
                        }
                        callback()
                    },
                    trigger: 'blur',
                },
                ttl: [
                    {
                        required: true,
                        message: 'Please provide an engagement expiration time for the agent',
                        trigger: 'blur'
                    }
                ],
                default_outbound_campaign_id: {
                    validator: (_, value, callback) => {
                        // Only require this field when default_outbound_type is OUTBOUND_TYPE_PREDEFINED
                        if (this.default_outbound_type === AloAi.OUTBOUND_TYPE_PREDEFINED) {
                            if (!value) {
                                callback(new Error('Please select a default outbound line'))
                                return
                            }
                        }
                        callback()
                    },
                    trigger: 'change',
                },
                instructions: {
                    validator: (_, value, callback) => {
                        // Check if instructions array exists and has at least one element
                        if (!this.bot.instructions || this.bot.instructions.length === 0) {
                            callback(new Error('Please provide instructions for the agent'))
                            return
                        }

                        // Trim the content of the first instruction
                        let content = this.bot.instructions[0]?.content?.trim()

                        let length = content?.length
                        if (!length) {
                            callback(new Error('Please provide instructions for the agent'))
                            return
                        }

                        callback()
                    },
                    trigger: 'blur',
                }
            },
            tab_errors: {
                configure: false,
                instructions: false,
                actions: false,
                post_call_actions: false,
                schedule: false,
                advanced: false,
                context: false
            },
            error_notification: null,
            activeName: 'configure',
            AloAi,
            default_outbound_type: AloAi.OUTBOUND_TYPE_LAST_LINE,
        }
    },
    computed: {
        ...mapState(['campaigns']),
        ...mapState('aloai', ['tools']),

        dialogTitle() {
            return this.bot.id ? `#${this.bot.id} ${this.bot.name}` : 'Create a new agent'
        },
        dialogSubtitle() {
            const type = this.bot.type === AloAi.TYPE_TEXT ? 'Text' : 'Voice'
            const direction = this.bot.direction === AloAi.DIRECTION_INBOUND ? 'Inbound' : 'Outbound'
            const model = this.bot.type === AloAi.TYPE_TEXT ? this.bot.model_human_readable_text : this.bot.voice_model_human_readable_text
            
            // Only show enabled status when updating existing bot
            const isUpdate = !!this.bot.id
            const enabled = isUpdate ? (this.bot.enabled ? 'Enabled' : 'Disabled') : null
            const parts = [type, direction]
            
            if (model) {
                parts.push(model)
            }
            
            if (enabled) {
                parts.push(enabled)
            }
            
            return parts.join(' - ')
        },
        submitButtonText() {
            if (this.loading) return 'Saving...'
            if (!this.isFormLoaded) return 'Loading...'
            return this.bot.id ? 'Save' : 'Create'
        },
        isFormLoaded() {
            return !this.dataLoading &&
                   !this.botLoading
        },
        
        availableTabs() {
            if (this.bot.is_system_bot && this.bot.internal_type === AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER) {
                return ['configure', 'instructions'];
            }
            
            const baseTabs = ['configure', 'instructions', 'context', 'schedule', 'custom_functions'];
            const tabs = [...baseTabs];
            
            // Actions tab - only for text bots
            if (this.bot.type === AloAi.TYPE_TEXT) {
                tabs.push('actions');
            }
            
            // Post Call Actions and Advanced tabs - only for voice bots
            if (this.bot.type === AloAi.TYPE_VOICE) {
                tabs.push('post_call_actions', 'advanced');
            }
            
            return tabs;
        }
    },
    methods: {
        ...mapActions('aloai', ['fetchTools', 'fetchAllVoiceData']),

        gotoBots() {
            this.$router.push({name: 'AloAi Agents'})
        },
        async connectInboundLines(botId, campaignIds) {
            if (!campaignIds || campaignIds.length === 0) {
                return
            }
            
            try {
                await axios.put(`/api/v1/aloai/bot/${botId}/update-inbound-lines`, {
                    add_campaign_ids: campaignIds,
                    remove_campaign_ids: []
                })
                
                this.$notify.success({
                    offset: 95,
                    title: 'Lines Connected',
                    message: `${campaignIds.length} inbound line(s) successfully connected to this agent.`,
                    duration: 3000
                })
            } catch (error) {
                this.$notify.error({
                    offset: 95,
                    title: 'Connection Failed',
                    message: error.response?.data?.message || 'Failed to connect the inbound lines',
                    duration: 5000
                })
                throw error
            }
        },

        submitForm() {
            this.loading = true
            this.resetTabErrors()
            this.closeErrorNotification()

            if (this.validateForm('agent_settings') === true) {
                // Get processed bot data from prompt-tab component
                const processedBot = this.$refs.promptTab ? this.$refs.promptTab.getProcessedBotForSaving() : this.bot

                // Determine if we're creating or updating
                const isUpdate = !!this.bot.id
                const method = isUpdate ? 'put' : 'post'
                const url = isUpdate ? `/api/v1/aloai/bot/${this.bot_id}` : '/api/v1/aloai/bot'
                const successMessage = isUpdate ? 'Agent updated successfully.' : 'Agent created successfully.'
                const errorAction = isUpdate ? 'updating' : 'creating'

                return axios[method](url, processedBot)
                .then(async response => {
                    this.$notify.success({
                        offset: 95,
                        title: 'AloAi Agent',
                        message: successMessage,
                    })

                    // For create operations, handle pending inbound lines connection
                    if (!isUpdate && this.pendingInboundCampaignIds && this.pendingInboundCampaignIds.length > 0) {
                        try {
                            await this.connectInboundLines(response.data.id, this.pendingInboundCampaignIds)
                        } catch (error) {
                            // Log error but don't block navigation
                            console.error('Failed to connect inbound lines:', error)
                        }
                    }

                    // For create operations, update local bot data and redirect
                    if (!isUpdate) {
                        this.bot = response.data
                        this.$router.push({
                            name: 'AloAi: Configure an agent',
                            params: {
                                bot_id: response.data.id
                            }
                        })
                    }

                    this.snapshotBot()

                    return true
                })
                .catch(error => {
                    const errorMessage = error.response?.data?.message
                        ? `There was an error ${errorAction} the bot: ${error.response?.data?.message}`
                        : `There was an error ${errorAction} the bot`
                    this.$notify.error({
                        offset: 95,
                        title: 'AloAi Agent',
                        message: errorMessage
                    })

                    return false
                })
                .finally(() => {
                    setTimeout(() => {
                        this.loading = false
                    }, 1500)
                })
            } else {
                this.error_notification = this.$notify({
                    offset: 95,
                    title: 'AloAi Agents',
                    dangerouslyUseHTMLString: true,
                    message: this.getFormErrorsMessage('agent_settings'),
                    type: 'error',
                    showClose: true,
                    duration: 5000,
                })
                this.loading = false

                return Promise.resolve(false)
            }
        },
        fetchBot(bot_id) {
            this.botLoading = true
            axios.get(`/api/v1/aloai/bot/${bot_id}`).then(res => {
                // Merge the default settigns with the API settings received
                const settings = {
                    ...this.bot.settings,
                    ...res.data.settings,
                }

                // Update existing reactive object instead of replacing it to preserves Vue's reactivity tracking
                Object.assign(this.bot, res.data)
                this.bot.settings = settings

                // Set default_outbound_type based on fetched bot data
                if (this.bot.default_outbound_campaign_id) {
                    this.default_outbound_type = AloAi.OUTBOUND_TYPE_PREDEFINED
                } else {
                    this.default_outbound_type = AloAi.OUTBOUND_TYPE_LAST_LINE
                }

                // If voice agents are not enabled, redirect to the previous page
                // Exception: bots with internal_type can always be edited
                if (this.bot.type === AloAi.TYPE_VOICE 
                    && !this.bot.internal_type 
                    && !this.isAloAiVoiceAgentsEnabled()) {
                    this.goBack()
                }

                // If text agents are not enabled, redirect to the previous page
                if (this.bot.type === AloAi.TYPE_TEXT && !this.isAloAiTextAgentsEnabled()) {
                    this.goBack()
                }

                // Set tools array using UUIDs
                this.bot.tools = this.bot.tools.length ? this.bot.tools.map(tool => tool.uuid || tool) : []
            }).catch(err => {
                console.log(err)
                this.goBack()
            }).finally(() => {
                this.botLoading = false
                this.$nextTick(() => {
                    this.snapshotBot()
                })
            })
        },

        // Methods from settings.vue
        getFormErrorsMessage(form_name) {
            let form_element = _.get(this.$refs, form_name, null)
            let err_message = '<div class="mb-2">Please resolve AloAi agent configuration errors</div>'

            form_element.fields.forEach((element) => {
                if (element.validateState === 'error') {
                    // get the parent tab pane of the element
                    let parent = element.$parent

                    while (parent && parent.$options && parent.$options._componentTag !== 'el-tab-pane') {
                        parent = parent.$parent
                    }

                    const tab = parent && parent.$attrs ? (parent.$attrs['data-tab-error'] || parent.name.replace('-', '_')) : ''
                    const tab_label = parent && parent.$slots && parent.$slots.label && parent.$slots.label[0] && parent.$slots.label[0].children && parent.$slots.label[0].children[0] ? parent.$slots.label[0].children[0].text : 'Unknown'

                    // show the error icon on the element's parent tab pane
                    if (tab !== "") {
                        this.tab_errors[tab] = true
                    }

                    err_message += `<div class="mb-2"><i class="fa fa-remove text-danger mr-2"></i> ${tab_label} > ${element.validateMessage}</div>`
                }
            })

            return err_message
        },

        tabLabelErrorIcon(tab_error) {
            if (tab_error) {
                return ' <i class="fa fa-exclamation-circle text-danger ml-1"></i>'
            }

            return ''
        },

        resetTabErrors() {
            this.tab_errors = {
                configure: false,
                instructions: false,
                knowledge_base: false,
                actions: false,
                post_call_actions: false,
                schedule: false,
                advanced: false,
                context: false
            }
        },

        closeErrorNotification() {
            if (this.error_notification) {
                this.error_notification.close()
            }
        },

        // Validate specific form field manually
        validateField(field) {
            this.$refs.agent_settings.validateField(field)
        },

        snapshotBot() {
            this.savedBotSnapshot = JSON.stringify(this.bot)
        },

        isBotDirty() {
            if (!this.savedBotSnapshot) {
                return false
            }
            return JSON.stringify(this.bot) !== this.savedBotSnapshot
        },

        async handleTestCall() {
            if (this.isBotDirty()) {
                try {
                    await this.$confirm(
                        'You have unsaved changes. The agent will be saved before starting the test call.',
                        'Save Changes',
                        {
                            confirmButtonText: 'Save & Test',
                            cancelButtonText: 'Cancel',
                            type: 'warning',
                        }
                    )
                } catch {
                    return
                }

                const saved = await this.submitForm()
                if (!saved) {
                    return
                }
            }

            this.showTestCallDialog = true
        }
    },

    watch: {
        default_outbound_type: function (newVal) {
            if (newVal === AloAi.OUTBOUND_TYPE_LAST_LINE) {
                this.bot.default_outbound_campaign_id = null
            }
            // Trigger validation for the campaign field when type changes
            this.$nextTick(() => {
                this.validateField('default_outbound_campaign_id')
            })
        },
        'bot.direction': function (newDir) {
            if (newDir !== AloAi.DIRECTION_OUTBOUND) {
                this.bot.default_outbound_campaign_id = null
                this.default_outbound_type = AloAi.OUTBOUND_TYPE_LAST_LINE
            }
        }
    },
    created() {
        if (!this.shouldShowAloAi) {
            this.goBack()
        }
    },
    async mounted() {
        this.setPageTitle(this.$route.name)

        // Load AloAi data first
        try {
            await Promise.all([
                this.fetchTools(),
                this.fetchAllVoiceData()
            ])
        } catch (error) {
            console.error('Error loading AloAi data:', error)
        } finally {
            this.dataLoading = false
        }

        // Initialize default_outbound_type based on existing bot data
        if (this.bot.default_outbound_campaign_id) {
            this.default_outbound_type = AloAi.OUTBOUND_TYPE_PREDEFINED
        }

        if (this.bot_id) {
            // Fetch bot data after voice data is loaded
            await this.fetchBot(this.bot_id)
        } else {
            this.bot.type = this.$route.query.type === 'text' ? AloAi.TYPE_TEXT : AloAi.TYPE_VOICE
            this.bot.direction = this.$route.query.direction === 'outbound' ? AloAi.DIRECTION_OUTBOUND : AloAi.DIRECTION_INBOUND

            // Check if the text agents are enabled and redirect if not
            if (this.bot.type === AloAi.TYPE_TEXT && !this.isAloAiTextAgentsEnabled()) {
                this.goBack()
                return
            }

            // Check if the voice agents are enabled and redirect if not
            if (this.bot.type === AloAi.TYPE_VOICE && !this.isAloAiVoiceAgentsEnabled()) {
                this.goBack()
                return
            }

            // Check if the voice agents are enabled and redirect if not
            if (this.bot.type === AloAi.TYPE_VOICE) {
                // Check if the outbound voice agents are enabled and redirect if not
                if (this.bot.direction === AloAi.DIRECTION_OUTBOUND && !this.isAloAiOutboundVoiceAgentsEnabled()) {
                    this.goBack()
                    return
                }

                // Check if the inbound voice agents are enabled and redirect if not
                // Exception: bots with internal_type can always be edited
                if (this.bot.direction === AloAi.DIRECTION_INBOUND 
                    && !this.isAloAiInboundVoiceAgentsEnabled()
                    && !this.bot.internal_type) {
                    this.goBack()
                    return
                }
            }

            this.botLoading = false
        }
    },
    beforeRouteEnter(to, from, next) {
        auth.check().then((res) => {
                next()
        }).catch((err) => {
            next({
                name: 'Login',
                query: {
                    redirect: to.fullPath
                }
            })
        })
    },
    beforeRouteLeave(to, from, next) {
        this.closeErrorNotification()
        next()
    },
}
</script>

<style scoped>
.container-listing {
    padding: 0 1.5rem;
}

.container-header {
    z-index: 1002;
    background: white;
    padding: 20px 0;
    margin-bottom: 0;
}

.bot-title {
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 1.2;
}

.bot-subtitle {
    font-size: 1rem;
    color: #666;
    line-height: 1.2;
}
</style>
